import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as IconSearch } from "../Images/icon_search.svg";
import ApplyBtn from "../Components/ApplyBtn";
import MoreBtn from "../Components/MoreBtn";
import Layout from "../Layout/Layout";
import Pagination from "../Layout/Pagination";
import AlarmApply from "../Modals/AlarmApply";
import Rankings from "../Modals/Rankings";
import "../Styles/component.css";
import "../Styles/fonts.css";
import "../Styles/global.css";
import "../Styles/lists.css";
import {
  requestDeleteDepartmentBattle,
  requestDepartmentBattleList,
} from "./DepartmentBattle.api";
import { showToast } from "../Utils/variables";

const PAGE_SIZE = 10;

const formatToday = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value) => String(value ?? "").replaceAll("-", ".");

const getProgressStatus = (startDate, endDate) => {
  const today = formatToday();

  if (!startDate || !endDate) {
    return "";
  }

  if (today < startDate) {
    return "진행전";
  }

  if (today > endDate) {
    return "종료";
  }

  return "진행중";
};

const DepartmentBattleManageContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [totalEntries, setTotalEntries] = useState(0);
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [searchTitle, setSearchTitle] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [clickDelete, setClickDelete] = useState(false);
  const [clickInput, setClickInput] = useState(false);
  const [clickRankings, setClickRankings] = useState(false);
  const [rankingId, setRankingId] = useState(null);
  const [rankingTitle, setRankingTitle] = useState("");
  const [rankingPeriod, setRankingPeriod] = useState("");
  const lastDepartmentBattleRequestKey = useRef(null);

  const getDepartmentBattleList = async (targetPage = page) => {
    try {
      const response = await requestDepartmentBattleList({
        page: targetPage,
        offset: PAGE_SIZE,
        searchTitle,
        navigate,
      });

      if (response.code === 200) {
        setTotalEntries(response.data.count);
        setEntries(response.data.rows);
      }
    } catch (error) {
      console.log("부서별 대항전 리스트 가져오기 중 에러:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (location.state?.toast_enable) {
      showToast(
        location.state.toast_message ?? "부서별 대항전이 저장되었습니다.",
        "success"
      );
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const requestKey = JSON.stringify({ page });

    if (lastDepartmentBattleRequestKey.current === requestKey) {
      return;
    }

    lastDepartmentBattleRequestKey.current = requestKey;
    getDepartmentBattleList();
  }, [page]);

  const handleSearch = () => {
    if (page === 1) {
      getDepartmentBattleList(1);
      return;
    }

    setPage(1);
  };

  const handleDelete = async () => {
    try {
      const response = await requestDeleteDepartmentBattle({
        seasonDepartmentId: selectedId,
        navigate,
      });

      if (response.code === 200) {
        setClickDelete(false);
        showToast("부서별 대항전이 삭제되었습니다.", "success");

        if (entries.length === 1 && page > 1) {
          setPage((prev) => prev - 1);
          return;
        }

        getDepartmentBattleList(page);
      }
    } catch (error) {
      console.log("부서별 대항전 삭제 중 에러:", error);
      throw error;
    }
  };

  const handleClickRanking = (seasonDepartmentId, title, startDate, endDate) => {
    setRankingId(seasonDepartmentId);
    setRankingTitle(title);
    setRankingPeriod(
      `${formatDisplayDate(startDate)} ~ ${formatDisplayDate(endDate)}`
    );
    setClickRankings(true);
  };

  return (
    <div>
      {clickRankings && (
        <Rankings
          title="랭킹 간략히 보기"
          infotext="부서별 대항전은 목표 달성률 기준으로 랭킹이 산정됩니다."
          contestType="부서별 대항전"
          contestTitle={rankingTitle}
          contestPeriod={rankingPeriod}
          ourcontestType=""
          id={rankingId}
          handleClickX={() => setClickRankings(false)}
        />
      )}
      {clickDelete && (
        <AlarmApply
          title="항목을 삭제하시겠습니까?"
          subtitle="삭제하면 앱과 대시보드 내에서 해당 데이터가 숨김 처리됩니다."
          handleApply={handleDelete}
          handleClickX={() => setClickDelete(false)}
        />
      )}
      <ManageBox>
        <div className="title-in-box">부서별 대항전 관리</div>

        <div className="hrz-wrapper" style={{ marginTop: "20px" }}>
          <div
            className="searchBox"
            style={{ boxShadow: clickInput ? "inset 0 0 0 1px #1167ed" : "" }}
          >
            <input
              type="text"
              placeholder="대회명"
              value={searchTitle}
              onChange={(event) => setSearchTitle(event.target.value)}
              onClick={() => setClickInput(true)}
              onBlur={() => setClickInput(false)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <IconSearch style={{ cursor: "pointer" }} onClick={handleSearch} />
          </div>

          <div style={{ display: "flex", marginLeft: "auto" }}>
            <ApplyBtn addr="/departmentBattle/apply" />
          </div>
        </div>

        <div
          className="hrz-wrapper"
          style={{ marginTop: "30px", color: "#000000" }}
        >
          전체 {totalEntries.toLocaleString()}건
        </div>

        <div className="listArea" style={{ marginTop: "10px" }}>
          <div
            className="list_titleBar"
            style={{
              gridTemplateColumns:
                "100px 140px 300px 120px 220px 220px 140px 260px",
            }}
          >
            <p className="center">NO</p>
            <p className="leftalign">대회 이미지</p>
            <p className="leftalign">대회명</p>
            <p className="leftalign">진행 상태</p>
            <p className="leftalign">대회 기간</p>
            <p className="leftalign">등록일</p>
          </div>

          {entries.length > 0 ? (
            entries.map((entry) => {
              const progressStatus = getProgressStatus(
                entry.startDate,
                entry.endDate
              );

              return (
                <div
                  key={entry.seasonDepartmentId}
                  className="list_contentBar"
                  style={{
                    gridTemplateColumns:
                      "100px 140px 300px 120px 220px 220px 290px 58px 52px",
                  }}
                >
                  <p className="center">{entry.rownum?.toLocaleString()}</p>
                  <div className="leftalign">
                    <ThumbnailBox>
                      {entry.imageUrl ? (
                        <img
                          src={`${process.env.REACT_APP_IMAGE_BASE_URL}/${entry.imageUrl}`}
                          alt={entry.title}
                        />
                      ) : (
                        <ThumbnailFallback>
                          {entry.title.slice(0, 4)}
                        </ThumbnailFallback>
                      )}
                    </ThumbnailBox>
                  </div>
                  <div className="scrollName leftalign">
                    <p style={{ whiteSpace: "nowrap" }}>{entry.title}</p>
                  </div>
                  <p className="leftalign">{progressStatus}</p>
                  <p className="leftalign">
                    {entry.startDate} ~ {entry.endDate}
                  </p>
                  <p className="leftalign">{entry.regTime}</p>
                  <div />
                  <div className="center">
                    {progressStatus !== "진행전" && (
                      <div
                        className="blueBorderBtn-58x39 center"
                        onClick={() =>
                          handleClickRanking(
                            entry.seasonDepartmentId,
                            entry.title,
                            entry.startDate,
                            entry.endDate
                          )
                        }
                      >
                        랭킹
                      </div>
                    )}
                  </div>
                  <div className="center">
                    <MoreBtn
                      option1="수정"
                      option2="삭제"
                      option1Addr={`/departmentBattle/edit/${entry.seasonDepartmentId}`}
                      optionClickDelete={() => {
                        setSelectedId(entry.seasonDepartmentId);
                        setClickDelete(true);
                      }}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <EmptyState>
              {searchTitle.trim()
                ? "검색 결과가 없습니다."
                : "등록된 부서별 대항전이 없습니다."}
            </EmptyState>
          )}
        </div>

        <Pagination
          totalPages={
            totalEntries === 0 ? 1 : Math.ceil(totalEntries / PAGE_SIZE)
          }
          currentPage={page}
          onPageChange={(pageNum) => setPage(pageNum)}
        />
      </ManageBox>
    </div>
  );
};

const ManageBox = styled.div`
  width: 1500px;
  height: auto;
  border-radius: 20px;
  margin: 30px;
  padding: 30px;
  background-color: #ffffff;
`;

const ThumbnailBox = styled.div`
  width: 88px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #d9d9d9;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ThumbnailFallback = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #dbe8fd 0%, #c6d9f5 100%);
  color: #1167ed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "notoSans-bold";
  font-size: 12px;
`;

const EmptyState = styled.div`
  width: 1500px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888888;
  box-shadow: inset 0 -1px 0 0 #e4e4e4;
`;

const DepartmentBattleManage = () => {
  return <Layout page={<DepartmentBattleManageContent />} />;
};

export default DepartmentBattleManage;
